import { parsePhone } from '../../core/string-helpers.ts'
import type { EquipmentAccessorial } from '../../equipment/accessorials.ts'
import { equipmentTypes } from '../../equipment/equipment-types.ts'
import { validateEquipmentAccessorials } from '../../equipment/utils.ts'
import {
  vBoolean,
  vCurrency,
  vDateUtcNoon,
  vEmailOrEmpty,
  vInt,
  vMetadata,
  vMilitaryTimeStrict,
  vMinValue,
  vNumber,
  vObject,
  vOptional,
  vOptionalDef,
  type vOutput,
  vPhoneExt,
  vPicklist,
  vPipe,
  vPostalCode,
  vRawCheck,
  vString,
  vTransform,
} from '../../validation/index.ts'
import { CountryMapper } from '../country.ts'
import { customsBrokerTypes } from '../customs-broker.ts'
import { stopDateTypes, stopTypes } from '../load-location.ts'
import { packagingTypes } from '../packaging-type.ts'
import {
  createBOLSchema,
  defaultFlagSchema,
  intSchema,
  selectQuoteSchema,
  shipTypeSchema,
  strictCloseDockHoursSchema,
  strictOpenDockHoursSchema,
} from './common.ts'
import { flatItemSchema } from './flat-item.ts'

const EQUIPMENT_TYPE_ERROR = 'Invalid equipment type provided'

const phoneSchema = vOptional(vPipe(vString(), vTransform(parsePhone)))
const countrySchema = vOptionalDef(
  vPipe(
    vString(),
    vTransform(value =>
      value ? CountryMapper.legacyToCore(value as string) : undefined,
    ),
  ),
  'us',
)

type PadEntries<T, Prefix extends string, Suffix extends string> = {
  [K in keyof T as K extends string ? `${Prefix}${K}${Suffix}` : never]: T[K]
}

export const tlFlatItemSchema = vObject({
  ...flatItemSchema.entries,
  itemPickupSequence: vOptional(vPipe(vNumber(), vInt(), vMinValue(0))),
  itemDropSequence: vOptional(vPipe(vNumber(), vInt(), vMinValue(1))),
  itemDeclaredValue: vOptional(vPipe(vNumber(), vMinValue(1))),
  itemDeclaredValueCurrency: vOptional(vCurrency()),
  itemPackagingType: vOptional(
    vPipe(
      vString(),
      vTransform(value => value?.toLowerCase()),
      vPicklist(
        packagingTypes
          .filter(p => p.modes.includes('truckload'))
          .map(p => p.key),
      ),
    ),
  ),
})

const getItemSchema = <Suffix extends string>(suffix: Suffix) => {
  return vObject<PadEntries<typeof tlFlatItemSchema.entries, '', Suffix>>(
    Object.entries(tlFlatItemSchema.entries).reduce((acc, [key, value]) => {
      acc[`${key}${suffix}`] = value
      return acc
    }, {} as any),
  )
}

const flatCustomsBrokerSchema = vObject({
  customsBrokerCompanyName: vOptional(vString()),
  customsBrokerAddress: vOptional(vString()),
  customsBrokerAddress2: vOptional(vString()),
  customsBrokerAddress3: vOptional(vString()),
  customsBrokerCity: vOptional(vString()),
  customsBrokerState: vOptional(vString()),
  customsBrokerPostalCode: vOptional(vPostalCode(true)),
  customsBrokerCountry: countrySchema,
  customsBrokerPhone: phoneSchema,
  customsBrokerPhoneExt: vOptional(vPhoneExt()),
  customsBrokerEmail: vOptional(vEmailOrEmpty),
  customsBrokerDeclaredValue: vOptional(vNumber()),
  customsBrokerType: vOptional(vPicklist(customsBrokerTypes)),
})

const getCustomsBrokerSchema = <Suffix extends string>(suffix: Suffix) => {
  return vObject<
    PadEntries<typeof flatCustomsBrokerSchema.entries, '', Suffix>
  >(
    Object.entries(flatCustomsBrokerSchema.entries).reduce(
      (acc, [key, value]) => {
        acc[`${key}${suffix}`] = value
        return acc
      },
      {} as any,
    ),
  )
}

const flatStopSchema = vObject({
  StopDate: vOptional(vDateUtcNoon()),
  StopDateType: vOptional(
    vPipe(
      vString(),
      vTransform(value => value?.toLowerCase()),
      vPicklist(stopDateTypes),
    ),
  ),
  Company: vOptional(vString()),
  Address: vOptional(vString()),
  Address2: vOptional(vString()),
  Address3: vOptional(vString()),
  City: vOptional(vString()),
  State: vOptional(vString()),
  PostalCode: vOptional(vPostalCode()),
  Country: countrySchema,
  LocationType: vOptional(shipTypeSchema),
  ContactName: vOptional(vString()),
  ContactPhone: phoneSchema,
  ContactPhoneExt: vOptional(vPhoneExt()),
  ContactEmail: vOptional(vEmailOrEmpty),
  ReferenceNumber: vOptional(vString()),
  Instructions: vOptional(vString()),
  DockHoursOpen: strictOpenDockHoursSchema,
  DockHoursClose: strictCloseDockHoursSchema,
  DriverAssistFlag: defaultFlagSchema,
  ArrivalNoticeFlag: defaultFlagSchema,
  TWICRequiredFlag: defaultFlagSchema,
  AppointmentFlag: vOptional(vBoolean()),
  AppointmentStart: vOptional(vMilitaryTimeStrict),
  AppointmentEnd: vOptional(vMilitaryTimeStrict),
})

const getStopSchema = <INum extends string>(suffix: INum) => {
  return vObject<PadEntries<typeof flatStopSchema.entries, 'stop', INum>>(
    Object.entries(flatStopSchema.entries).reduce(
      (acc, [key, value]) => {
        acc[`stop${key}${suffix}`] = value
        return acc
      },
      {
        [`stopType${suffix}`]: vOptional(vPicklist(stopTypes)),
      } as any,
    ),
  )
}

export const originSchema = vObject<
  PadEntries<typeof flatStopSchema.entries, 'origin', ''>
>({
  ...Object.entries(flatStopSchema.entries).reduce((acc, [key, value]) => {
    acc[`origin${key}`] = value
    return acc
  }, {} as any),
  originPostalCode: vPostalCode(),
  originStopDate: vDateUtcNoon(),
})

export const destinationSchema = vObject<
  PadEntries<typeof flatStopSchema.entries, 'dest', ''>
>({
  ...Object.entries(flatStopSchema.entries).reduce((acc, [key, value]) => {
    acc[`dest${key}`] = value
    return acc
  }, {} as any),
  destPostalCode: vPostalCode(),
})

export const baseTlFlatLoadSchema = vObject({
  timezone: vPipe(vOptional(vString()), vMetadata({ hidden: true })),
  bolNumber: vOptional(vString()),
  ...selectQuoteSchema.entries,
  ...createBOLSchema.entries,
  referenceNumber: vOptional(vString()),
  equipmentType: vPicklist(
    equipmentTypes.map(e => e.key),
    EQUIPMENT_TYPE_ERROR,
  ),
  description: vOptional(vString()),
  weight: vOptional(vNumber()),
  declaredValue: vOptional(vNumber()),
  liveLoadFlag: defaultFlagSchema,

  bidExpirationDate: vOptional(vDateUtcNoon()),
  bidExpirationTime: vOptional(vMilitaryTimeStrict),
  targetPrice: vOptional(vNumber()),
  spotQuoteMessage: vOptional(vString()),

  ...originSchema.entries,
  ...destinationSchema.entries,

  emergencyName: vOptional(vString()),
  emergencyPhone: phoneSchema,
  emergencyPhoneExt: vOptional(vPhoneExt()),
  emergencyContractNumber: vOptional(vString()),

  hazardousFlag: defaultFlagSchema,
  eTrackFlag: defaultFlagSchema,
  foodGradeFlag: defaultFlagSchema,
  liftgateFlag: defaultFlagSchema,
  teamDriverFlag: defaultFlagSchema,
  tankerEndorsementFlag: defaultFlagSchema,
  pipeStakesFlag: defaultFlagSchema,
  loadBarsCount: vOptional(intSchema),
  chainsCount: vOptional(intSchema),
  tarpsSize: vOptional(vPicklist([4, 6, 8])),
  strapsCount: vOptional(intSchema),
  maxTemp: vOptional(intSchema),
  minTemp: vOptional(intSchema),
  tempUOM: vOptional(vPicklist(['f', 'c'])),

  ...getItemSchema('1').entries,
  ...getItemSchema('2').entries,
  ...getItemSchema('3').entries,
  ...getItemSchema('4').entries,
  ...getItemSchema('5').entries,
  ...getItemSchema('6').entries,
  ...getItemSchema('7').entries,
  ...getItemSchema('8').entries,
  ...getItemSchema('9').entries,
  ...getItemSchema('10').entries,

  alternateType1: vOptional(
    vPicklist(
      equipmentTypes.map(e => e.key),
      EQUIPMENT_TYPE_ERROR,
    ),
  ),
  alternateType2: vOptional(
    vPicklist(
      equipmentTypes.map(e => e.key),
      EQUIPMENT_TYPE_ERROR,
    ),
  ),
  alternateType3: vOptional(
    vPicklist(
      equipmentTypes.map(e => e.key),
      EQUIPMENT_TYPE_ERROR,
    ),
  ),
  alternateType4: vOptional(
    vPicklist(
      equipmentTypes.map(e => e.key),
      EQUIPMENT_TYPE_ERROR,
    ),
  ),
  alternateType5: vOptional(
    vPicklist(
      equipmentTypes.map(e => e.key),
      EQUIPMENT_TYPE_ERROR,
    ),
  ),

  billToCompany: vOptional(vString()),
  billToAddress: vOptional(vString()),
  billToAddress2: vOptional(vString()),
  billToAddress3: vOptional(vString()),
  billToCity: vOptional(vString()),
  billToState: vOptional(vString()),
  billToPostalCode: vOptional(vPostalCode()),
  billToCountry: countrySchema,
  billToContactName: vOptional(vString()),
  billToContactPhone: phoneSchema,
  billToContactPhoneExt: vOptional(vPhoneExt()),
  billToContactEmail: vOptional(vEmailOrEmpty),

  ...getCustomsBrokerSchema('1').entries,
  ...getCustomsBrokerSchema('2').entries,

  ...getStopSchema('1').entries,
  ...getStopSchema('2').entries,
  ...getStopSchema('3').entries,
  ...getStopSchema('4').entries,
  ...getStopSchema('5').entries,
  ...getStopSchema('6').entries,
  ...getStopSchema('7').entries,
  ...getStopSchema('8').entries,
  ...getStopSchema('9').entries,
  ...getStopSchema('10').entries,
})

export const tlFlatLoadSchema = vPipe(
  baseTlFlatLoadSchema,
  vRawCheck(({ dataset, addIssue }) => {
    if (dataset.typed) {
      const load = dataset.value
      const equipmentTypes = [
        dataset.value.equipmentType,
        dataset.value.alternateType1,
        dataset.value.alternateType2,
        dataset.value.alternateType3,
        dataset.value.alternateType4,
        dataset.value.alternateType5,
      ].filter(Boolean)

      const accessorialErrors = validateEquipmentAccessorials(
        equipmentTypes,
        mapEquipmentAccessorials(load),
      )

      if (accessorialErrors.length > 0) {
        accessorialErrors.forEach(error => {
          addIssue({
            message: error,
          })
        })
      }
    }
  }),
)

export const mapEquipmentAccessorials = (
  flatLoad: TlFlatLoad,
): EquipmentAccessorial[] => {
  const accessorials: EquipmentAccessorial[] = []

  if (flatLoad.chainsCount) {
    accessorials.push({ key: 'chains', count: flatLoad.chainsCount })
  }

  if (flatLoad.tarpsSize) {
    accessorials.push({ key: 'tarps', size: flatLoad.tarpsSize })
  }

  if (flatLoad.strapsCount) {
    accessorials.push({ key: 'straps', count: flatLoad.strapsCount })
  }

  if (flatLoad.maxTemp && flatLoad.minTemp && flatLoad.tempUOM) {
    accessorials.push({
      key: 'temp',
      min: flatLoad.minTemp,
      max: flatLoad.maxTemp,
      uom: flatLoad.tempUOM,
    })
  }

  if (flatLoad.eTrackFlag) {
    accessorials.push({ key: 'e-track' })
  }

  if (flatLoad.tankerEndorsementFlag) {
    accessorials.push({ key: 'tanker-endorsement' })
  }

  if (flatLoad.pipeStakesFlag) {
    accessorials.push({ key: 'pipe-stakes' })
  }

  if (flatLoad.loadBarsCount) {
    accessorials.push({ key: 'load-bars', count: flatLoad.loadBarsCount })
  }

  if (flatLoad.teamDriverFlag) {
    accessorials.push({ key: 'team-driver' })
  }

  if (flatLoad.foodGradeFlag) {
    accessorials.push({ key: 'food-grade' })
  }

  if (flatLoad.liftgateFlag) {
    accessorials.push({ key: 'liftgate' })
  }

  return accessorials
}

export type TlFlatLoad = vOutput<typeof tlFlatLoadSchema>
